import { useQueryClient } from '@tanstack/react-query';
import type {
  CreatePendingPaymentDto,
  CreatePendingPaymentRequest,
  ErrorMessage,
  InitiatePendingPaymentResponse,
  PayByAmountDigitalBillDto,
  PayCartDto,
} from '../../../generated-api';

import { useMutateCreatePendingPayment } from './usePendingPaymentApi';
import { useCallback } from 'react';

interface InitiatePendingPaymentArgs {
  locationId: string;
  amount: number;
  orderReference?: string;
  deviceId: string;
  paymentMethodId: string;
  payCartDto?: PayCartDto;
  payByAmountDigitalBillDto?: PayByAmountDigitalBillDto;
  askForTip: boolean;
  currency: string;
  onSuccess?: (response: InitiatePendingPaymentResponse) => void;
  onError?: (error?: ErrorMessage) => void;
}

const useCreatePendingPayment = () => {
  const { mutate: initiatePendingPayment, isPending } = useMutateCreatePendingPayment();
  const queryClient = useQueryClient();

  const createPendingPayment = useCallback(
    async (props: InitiatePendingPaymentArgs) => {
      const createPendingPaymentDto: CreatePendingPaymentDto = {
        amount: props.amount,
        askForTip: props.askForTip,
        currency: props.currency,
        deviceId: props.deviceId,
        locationId: props.locationId,
        orderReference: props.orderReference,
        payByAmountDigitalBillDto: props.payByAmountDigitalBillDto,
        payCartDto: props.payCartDto,
        paymentMethodId: props.paymentMethodId,
      };

      const createPendingPaymentRequest: CreatePendingPaymentRequest = {
        createPendingPaymentDto,
        locationId: props.locationId,
        paymentMethodId: props.paymentMethodId,
      };

      initiatePendingPayment(createPendingPaymentRequest, {
        onError: (error) => {
          if (props.onError) {
            props.onError(error);
          }
        },
        onSettled: () =>
          queryClient.refetchQueries({
            queryKey: ['active-payments', props.locationId],
          }),
        onSuccess: (data) => {
          if (props.onSuccess) {
            props.onSuccess(data);
          }
        },
      });
    },
    [initiatePendingPayment, queryClient]
  );

  return {
    createPendingPayment,
    isWorking: isPending,
  };
};

export default useCreatePendingPayment;
